/**
 * AcceptInvitePage — public self-serve flow: set password (and optional name) after email link.
 *
 * Purpose: Reads `token` from query string, collects name + password with plain TextFields
 *          (the password pair is the shared NewPasswordFields)
 *          (the password must meet the password policy, shown and checked as it is typed),
 *          (no authenticated API calls — TemplateForm was causing 401 → redirect-to-login),
 *          posts to POST /users/invitations/accept, then redirects to login.
 */

import React, { useCallback, useState } from 'react';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom';
import { Alert, Box, Button, Card, CardContent, Link, TextField, Typography } from '@mui/material';
import userManagementService from '../../services/userManagementService';
import NewPasswordFields, { newPasswordError } from '../../components/auth/NewPasswordFields';

const AcceptInvitePage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token')?.trim() ?? '';

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const canSubmit = Boolean(token);

  const handleSubmit = useCallback(async () => {
    setError(null);
    if (!token) {
      setError('Invalid or missing invitation link. Request a new invite from your administrator.');
      return;
    }
    const problem = newPasswordError(password, confirmPassword);
    if (problem) {
      setError(problem);
      return;
    }

    setIsSubmitting(true);
    try {
      await userManagementService.acceptInvitation({
        token,
        password,
        first_name: firstName.trim() || undefined,
        last_name: lastName.trim() || undefined,
      });
      navigate('/login', { replace: true, state: { inviteAccepted: true } });
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { detail?: unknown } }; message?: string };
      let message = ax?.message || 'Could not complete registration.';
      const detail = ax?.response?.data?.detail;
      if (typeof detail === 'string') {
        message = detail;
      } else if (detail && typeof detail === 'object' && 'message' in detail) {
        message = String((detail as { message: string }).message);
      }
      setError(message);
    } finally {
      setIsSubmitting(false);
    }
  }, [confirmPassword, firstName, lastName, navigate, password, token]);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 2,
        bgcolor: 'background.default',
      }}
    >
      <Card sx={{ maxWidth: 480, width: '100%' }} elevation={3}>
        <CardContent sx={{ p: 3 }}>
          <Typography variant="h5" component="h1" gutterBottom>
            Complete your account
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Create a password to finish setting up your OrchestrateHQ account. Your email and
            organization were assigned when you were invited.
          </Typography>

          {!canSubmit ? (
            <Alert severity="warning" sx={{ mb: 2 }}>
              This link is missing a token. Open the link from your invitation email, or ask an
              admin to send a new invite.
            </Alert>
          ) : null}

          {error ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          ) : null}

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 2 }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
              <TextField
                id="accept-invite-first-name"
                label="First name"
                value={firstName}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFirstName(e.target.value)}
                autoComplete="given-name"
                fullWidth
                disabled={!canSubmit || isSubmitting}
              />
              <TextField
                id="accept-invite-last-name"
                label="Last name"
                value={lastName}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setLastName(e.target.value)}
                autoComplete="family-name"
                fullWidth
                disabled={!canSubmit || isSubmitting}
              />
            </Box>
            <NewPasswordFields
              idPrefix="accept-invite"
              password={password}
              confirmPassword={confirmPassword}
              onPasswordChange={setPassword}
              onConfirmPasswordChange={setConfirmPassword}
              disabled={!canSubmit || isSubmitting}
            />
          </Box>

          <Button
            variant="contained"
            fullWidth
            size="large"
            sx={{ mt: 1 }}
            disabled={!canSubmit || isSubmitting}
            onClick={handleSubmit}
          >
            {isSubmitting ? 'Creating account…' : 'Create account'}
          </Button>

          <Box sx={{ mt: 2, textAlign: 'center' }}>
            <Link component={RouterLink} to="/login" variant="body2">
              Back to sign in
            </Link>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default AcceptInvitePage;
